import { FormikErrors } from 'formik'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalProps } from '@context/ModalsContext'

import { DocumentFileUpload } from '@ui/components/FileUpload/DocumentFileUpload'
import { isFileSizeValid } from '@ui/components/FileUpload/FileUpload'
import { FileData } from '@ui/components/FileUpload/types'
import { Modal } from '@ui/components/Modal'

import { DocumentCategorySizeDto, DocumentCategoryType } from '@model/DocumentCategories'
import { DocumentClientAreaDto } from '@model/DocumentClientAreaDto'

import { Box } from '@shared/components/Box'

export interface FormValuesWithDocuments {
  documents?: Record<string, FileData[]>
  [key: string]: any
}

interface DocumentUploadModalProps<T extends FormValuesWithDocuments> extends BaseModalProps {
  apiDocuments?: DocumentClientAreaDto[]

  setValues(
    values: React.SetStateAction<T>,
    shouldValidate?: boolean
  ): Promise<void | FormikErrors<T>>

  values: T
  questionId: string
  categorySizes?: DocumentCategorySizeDto[]
}

const MAX_MB_FILE = 8

export const DocumentUploadModal = <T extends FormValuesWithDocuments>(
  props: DocumentUploadModalProps<T>
) => {
  const { questionId, setValues, close, values, categorySizes = [], apiDocuments = [] } = props
  const { t } = useTranslation()

  const existingFiles = (values.documents?.[questionId] || []).filter((x) => !!x?.fileName)

  const [files, setFiles] = useState<FileData[]>(existingFiles)
  const [isValid, setIsValid] = useState(false)

  const maxFileSizeInMb = getMaxFileSize(categorySizes)

  useEffect(() => {
    const areSizesValid = files.every((file) => isFileSizeValid(file.fileSize, maxFileSizeInMb))
    setIsValid(areSizesValid)
  }, [files, maxFileSizeInMb])

  const handleSave = () => {
    if (isValid) {
      setValues((prevValues) => ({
        ...prevValues,
        documents: {
          ...(prevValues.documents || {}),
          [questionId]: files,
        },
      }))
      close()
    }
  }

  const handleDelete = (fileId: string | undefined) => {
    if (!fileId) return
    setFiles((prev) => prev.filter((x) => x.id !== fileId))
  }

  const handleUpload = (newFiles: FileData[]) => {
    setFiles(newFiles)
  }

  return (
    <Modal size='lg'>
      <Modal.Header>{t('Sign up.Document Upload .JPG .JPEG, .PNG .PDF')}</Modal.Header>
      <Modal.Body>
        <Modal.Body.Text>
          {t(
            'Sign up.If available please upload any documentary evidence of trading history (e.g. trading statement)'
          )}
        </Modal.Body.Text>
        <Box fullWidth mb='x16'>
          <DocumentFileUpload
            documentCategory={DocumentCategoryType.AppropriatenessTest}
            files={files}
            multiple
            documents={apiDocuments}
            categorySizes={categorySizes}
            onRemove={(data) => handleDelete(data.id)}
            onUpload={handleUpload}
          />
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>{t('Cancel')}</Modal.Footer.Secondary>
        <Modal.Footer.Primary onClick={handleSave} disabled={!isValid}>
          {t('Sign up.Upload')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}

const getMaxFileSize = (categorySizes: DocumentCategorySizeDto[]): number => {
  return (
    categorySizes.find((category) => category.id === DocumentCategoryType.AppropriatenessTest)
      ?.maxUploadFileSizeInMb ?? MAX_MB_FILE
  )
}
